import { useTranslation } from 'next-i18next';
import { SyntheticEvent } from 'react';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { Box } from '@bridebook/ui';
import {
  IconFacebook,
  IconInstagram,
  IconLinkedin,
  IconPinterest,
  IconTiktok,
  IconTwitter,
  IconVimeo,
  IconYoutube,
} from '@bridebook/ui/src/icons';
import SectionHeading from '@bridebook/web/app-shared/components/supplier/sections/section-heading';
import { isDefined } from '@bridebook/web/app-shared/lib/supplier/formatters';
import { formatUrl } from '@bridebook/web/app-shared/lib/supplier/utils/formatUrl';
import { isCordovaApp } from '@bridebook/web/lib/utils';
import openInBrowser from '@bridebook/web/lib/utils/open-in-browser';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/location-contact-details/social.style';

interface IProps {
  supplier: ISupplier;
  onSocialMediaClick?: (title: string) => void;
}

type SocialType = keyof NonNullable<ISupplier['social']>;

interface Field {
  title: SocialType;
  url: string;
}

const Social = ({ supplier, onSocialMediaClick }: IProps) => {
  const { t } = useTranslation('supplier');

  const { social } = supplier;
  const itemsToRender: SocialType[] = [
    'facebook',
    'twitter',
    'instagram',
    'pinterest',
    'vimeo',
    'linkedin',
    'youtube',
    'blog',
    'tiktok',
  ];

  const followSupplierOnSocialMedia =
    (title: string = '', url?: string | null) =>
    (e: SyntheticEvent<any>) => {
      if (isCordovaApp()) {
        e.preventDefault();
        openInBrowser(url);
      }
      onSocialMediaClick?.(title);
    };

  const fields = itemsToRender
    .map((i) => ({ title: i, url: formatUrl(social?.[i]) }))
    .filter((val): val is Field => isDefined(val.url));

  if (fields.length === 0) return null;

  const iconSwitch = (title: string) => {
    switch (title) {
      case 'facebook':
        return <IconFacebook color="space" />;
      case 'twitter':
        return <IconTwitter color="space" />;
      case 'instagram':
        return <IconInstagram color="space" width={15} />;
      case 'pinterest':
        return <IconPinterest color="space" width={15} />;
      case 'linkedin':
        return <IconLinkedin color="space" />;
      case 'vimeo':
        return <IconVimeo color="space" />;
      case 'youtube':
        return <IconYoutube color="space" width={20} />;
      case 'tiktok':
        return <IconTiktok color="space" width={15} />;
      default:
        return null;
    }
  };

  const styles = componentStyles();

  return (
    <Box style={styles.wrapper}>
      <SectionHeading lineHeight={20} largeMargin>
        {t('locationContact.social')}
      </SectionHeading>
      <Box style={styles.networksWrap}>
        {fields.map((field) => (
          <Box
            as="a"
            style={styles.network}
            href={field.url}
            rel="nofollow noopener"
            target="_blank"
            title={field.title}
            key={field.title}
            onClick={followSupplierOnSocialMedia(field.title, field.url)}>
            {iconSwitch(field.title)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Social;
